import { lazy, Suspense, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Bot } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { useAgentBeta } from '../contexts/AgentBetaContext';

const AgentsWorkspace = lazy(() => import('../components/agents/AgentsWorkspace'));

/**
 * The Agents area: Nexus (the business's general manager) plus the specialist
 * bots it hands work to. Full-height below the app header; businesses with
 * agents switched off see a short note instead of the workspace.
 */
/** Phones can rubber-band the document even when this view already fills the screen. */
function useAgentsScrollLock() {
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const appContent = document.querySelector('[data-app-content]');
    const nodes = [html, body, root, appContent].filter((node): node is HTMLElement => !!node);

    const apply = () => {
      const lock = mq.matches;
      nodes.forEach((node) => node.classList.toggle('agents-scroll-lock', lock));
      if (lock) window.scrollTo(0, 0);
    };

    apply();
    mq.addEventListener('change', apply);
    return () => {
      mq.removeEventListener('change', apply);
      nodes.forEach((node) => node.classList.remove('agents-scroll-lock'));
    };
  }, []);
}

export default function AgentsPage() {
  const { selectedBusiness, loading } = useBusiness();
  const { accepted, loaded: betaLoaded, openAgents } = useAgentBeta();
  const location = useLocation();
  const enabled = selectedBusiness?.agents_enabled !== false;
  useAgentsScrollLock();

  useEffect(() => {
    if (!betaLoaded || accepted) return;
    openAgents(`${location.pathname}${location.search}`);
  }, [betaLoaded, accepted, openAgents, location.pathname, location.search]);

  if (!betaLoaded || !accepted) {
    return (
      <div
        data-agents-page
        className="flex min-h-0 flex-col items-center justify-center bg-zinc-950 px-8 text-center"
        style={{ height: 'calc(100dvh - env(safe-area-inset-top, 0px) - 3rem - var(--bottom-nav-height, 0px))' }}
      >
        {!betaLoaded ? (
          <p className="text-[13px] text-zinc-500">Loading agents…</p>
        ) : (
          <>
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10">
              <Bot className="h-7 w-7 text-zinc-200" />
            </span>
            <h2 className="mt-4 text-[18px] font-semibold text-zinc-100">Join the beta to use Agents</h2>
            <p className="mt-1 max-w-sm text-[13px] text-zinc-500">
              Accept the Agents terms before this page opens.
            </p>
            <button
              type="button"
              onClick={() => openAgents(`${location.pathname}${location.search}`)}
              className="mt-5 rounded-full bg-white px-4 py-2 text-sm font-semibold text-neutral-950"
            >
              Continue
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <div
      data-agents-page
      className="flex min-h-0 flex-col bg-zinc-950"
      style={{ height: 'calc(100dvh - env(safe-area-inset-top, 0px) - 3rem - var(--bottom-nav-height, 0px))' }}
    >
      {!loading && selectedBusiness && !enabled ? (
        <div className="flex flex-1 flex-col items-center justify-center p-8 text-center text-zinc-300">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10">
            <Bot className="h-7 w-7 text-zinc-200" />
          </span>
          <h2 className="mt-4 text-[18px] font-semibold text-zinc-100">Agents are switched off</h2>
          <p className="mt-1 max-w-sm text-[13px] text-zinc-500">
            The agent team is not enabled for this business. The voice and help assistant keep working as before.
          </p>
        </div>
      ) : (
        <Suspense fallback={<div className="flex flex-1 items-center justify-center text-[13px] text-zinc-500">Loading agents…</div>}>
          <AgentsWorkspace />
        </Suspense>
      )}
    </div>
  );
}
